import { createFileRoute } from '@tanstack/react-router'
import * as React from "react";
import { API_ADMIN_BASE_URL } from "@/lib/api-client";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import {
  Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious,
} from "@/components/ui/pagination";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { Search } from "lucide-react";

interface ProjectItem {
  id: string;
  uuid: string;
  name: string;
  slug: string;
  createdAt: string;
  user: { id: string; email: string; name: string };
  _count: { canvases: number };
}

const fmtDate = (d: string) => new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

function ProjectsPage() {
  const [projects, setProjects] = React.useState<ProjectItem[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [total, setTotal] = React.useState(0);
  const [search, setSearch] = React.useState("");
  const [page, setPage] = React.useState(1);
  const limit = 20;

  const fetchProjects = React.useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("limit", limit.toString());
      params.set("offset", ((page - 1) * limit).toString());
      if (search) params.set("search", search);
      const res = await fetch(`${API_ADMIN_BASE_URL}/api/admin/projects?${params}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      const json = await res.json();
      setProjects(json.data.projects);
      setTotal(json.data.total);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, [search, page]);

  React.useEffect(() => { fetchProjects(); }, [fetchProjects]);
  React.useEffect(() => { document.title = "Projects - Admin"; }, []);
  React.useEffect(() => { setPage(1); }, [search]);

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="bg-background h-full overflow-auto">
      <header>
        <div className="border-border flex h-12 shrink-0 items-center gap-2 border-b px-4 md:h-16 md:px-6">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem><BreadcrumbLink href="/dashboard">Dashboard</BreadcrumbLink></BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem><BreadcrumbPage>Projects</BreadcrumbPage></BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
        <div className="border-border border-b px-4 py-4 md:px-6 md:py-6">
          <h1 className="text-2xl font-bold tracking-tight md:text-3xl">Projects</h1>
          <p className="text-muted-foreground text-sm">{total.toLocaleString()} projects</p>
        </div>
      </header>
      <main className="flex-1 space-y-6 px-4 py-6 md:px-6">
        <div className="relative w-full md:w-[300px]">
          <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
          <Input placeholder="Search name or email..." className="pl-8" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div className="relative overflow-hidden rounded-lg border">
          <div className="overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Owner</TableHead>
                  <TableHead className="text-center">Canvases</TableHead>
                  <TableHead>Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={4} className="text-center py-8 text-muted-foreground">Loading...</TableCell></TableRow>
                ) : projects.length === 0 ? (
                  <TableRow><TableCell colSpan={4} className="text-center py-8 text-muted-foreground">No projects found</TableCell></TableRow>
                ) : projects.map((p) => (
                  <TableRow key={p.uuid} className="hover:bg-muted/50">
                    <TableCell className="font-medium">{p.name}</TableCell>
                    <TableCell><span className="text-sm">{p.user.email}</span></TableCell>
                    <TableCell className="text-center">{p._count.canvases}</TableCell>
                    <TableCell><span className="text-muted-foreground text-xs">{fmtDate(p.createdAt)}</span></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
        {totalPages > 1 && (
          <Pagination>
            <PaginationContent>
              <PaginationItem><PaginationPrevious onClick={() => setPage(Math.max(1, page - 1))} className={page === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"} /></PaginationItem>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pn: number; if (totalPages <= 5) pn = i + 1; else if (page <= 3) pn = i + 1; else if (page >= totalPages - 2) pn = totalPages - 4 + i; else pn = page - 2 + i;
                return (<PaginationItem key={pn}><PaginationLink onClick={() => setPage(pn)} isActive={page === pn} className="cursor-pointer">{pn}</PaginationLink></PaginationItem>);
              })}
              <PaginationItem><PaginationNext onClick={() => setPage(Math.min(totalPages, page + 1))} className={page === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"} /></PaginationItem>
            </PaginationContent>
          </Pagination>
        )}
      </main>
    </div>
  );
}

export const Route = createFileRoute('/dashboard/projects')({
  component: ProjectsPage,
})
